import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import {
  Calendar,
  Sparkles,
  Wrench,
  Hammer,
  Receipt,
  BarChart3,
  Users,
  ShieldCheck,
  RefreshCw,
  ArrowRight,
  Check,
  X as XIcon,
  Image as ImageIcon,
  Bell,
  FolderKanban,
  DollarSign,
  Lock,
  ChevronRight,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  Reusable atoms                                                            */
/* -------------------------------------------------------------------------- */

const GoldRule = () => (
  <div className="flex items-center gap-3" aria-hidden="true">
    <span className="h-px w-10 bg-gradient-to-r from-transparent to-amber-300/60" />
    <span className="w-1.5 h-1.5 rotate-45 bg-amber-300/80" />
    <span className="h-px w-10 bg-gradient-to-l from-transparent to-amber-300/60" />
  </div>
);

const SectionLabel = ({ children }) => (
  <span className="text-sm font-medium uppercase tracking-widest text-amber-300">
    {children}
  </span>
);

const BgGlow = ({ className = '' }) => (
  <div
    className={`pointer-events-none absolute rounded-full blur-3xl ${className}`}
    aria-hidden="true"
  />
);

/* -------------------------------------------------------------------------- */
/*  Code-rendered product mockups                                             */
/* -------------------------------------------------------------------------- */

/** A dark browser-frame chrome wrapping any product mockup. */
const MockFrame = ({ children, url = 'app.propveiw.com' }) => (
  <div className="rounded-xl border border-[#1f3a64] bg-[#0c1530]/90 shadow-2xl shadow-black/60 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#1f3a64] bg-[#070d1c]/70">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
      <div className="flex-1 flex justify-center">
        <span className="text-[10px] font-mono text-slate-500 bg-[#0c1530] px-3 py-1 rounded-full border border-[#1f3a64]">
          {url}
        </span>
      </div>
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </div>
);

/** Unified calendar mockup — mini month grid with colored event dots. */
const CalendarMock = () => {
  // 6 rows x 7 cols
  const events = {
    3: [{ c: 'bg-sky-500/80', t: 'Airbnb' }],
    4: [{ c: 'bg-sky-500/80', t: 'Airbnb' }],
    5: [{ c: 'bg-emerald-500/80', t: 'Clean' }],
    9: [{ c: 'bg-amber-400/80', t: 'HVAC' }],
    12: [
      { c: 'bg-sky-500/80', t: 'VRBO' },
      { c: 'bg-emerald-500/80', t: 'Clean' },
    ],
    13: [{ c: 'bg-sky-500/80', t: 'VRBO' }],
    17: [{ c: 'bg-violet-500/80', t: 'Reno' }],
    18: [{ c: 'bg-violet-500/80', t: 'Reno' }],
    19: [{ c: 'bg-violet-500/80', t: 'Reno' }],
    22: [{ c: 'bg-rose-500/80', t: 'Owner' }],
    27: [
      { c: 'bg-sky-500/80', t: 'Airbnb' },
      { c: 'bg-emerald-500/80', t: 'Clean' },
    ],
  };
  return (
    <MockFrame url="app.propveiw.com/calendar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-white font-manrope font-bold text-lg">February 2026</div>
          <div className="text-xs text-slate-400 mt-0.5">Sunset Beach House · 4 properties</div>
        </div>
        <div className="flex gap-2">
          <span className="text-[10px] px-2 py-1 rounded-full border border-sky-500/40 text-sky-300 bg-sky-500/10">Bookings</span>
          <span className="text-[10px] px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">Cleaning</span>
          <span className="text-[10px] px-2 py-1 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/10">Maint</span>
          <span className="text-[10px] px-2 py-1 rounded-full border border-violet-500/40 text-violet-300 bg-violet-500/10">Reno</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#1f3a64]/60 rounded-md overflow-hidden">
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <div key={`h-${d}-${i}`} className="bg-[#0c1530] text-slate-500 text-[10px] uppercase tracking-wider text-center py-2">
            {d}
          </div>
        ))}
        {Array.from({ length: 35 }).map((_, i) => {
          const day = i + 1;
          const evs = events[day] || [];
          const isCurrent = day === 12;
          return (
            <div
              key={`d-${day}`}
              className={`bg-[#0c1530] h-16 p-1.5 relative ${isCurrent ? 'ring-1 ring-amber-300/60' : ''}`}
            >
              <div className={`text-[11px] ${isCurrent ? 'text-amber-200 font-semibold' : 'text-slate-400'}`}>
                {day <= 31 ? day : ''}
              </div>
              <div className="mt-1 space-y-0.5">
                {evs.map((e, j) => (
                  <div key={`e-${day}-${j}`} className={`h-1.5 rounded-sm ${e.c}`} title={e.t} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </MockFrame>
  );
};

/** iCal sync mockup — external channels feeding a central hub */
const IcalSyncMock = () => (
  <MockFrame url="app.propveiw.com/calendar-sync">
    <div className="text-white font-manrope font-semibold text-base mb-1">Calendar Sync</div>
    <div className="text-xs text-slate-400 mb-5">Both-way iCal, refreshed every 15 minutes</div>
    <div className="grid grid-cols-[1fr,60px,1fr] items-center gap-3">
      <div className="space-y-2">
        {[
          { name: 'Airbnb', color: 'bg-rose-500/80', status: 'Synced 2m ago' },
          { name: 'VRBO', color: 'bg-sky-500/80', status: 'Synced 4m ago' },
          { name: 'Booking.com', color: 'bg-indigo-500/80', status: 'Synced 6m ago' },
          { name: 'Evolve', color: 'bg-emerald-500/80', status: 'Synced 9m ago' },
        ].map((c) => (
          <div key={c.name} className="flex items-center gap-3 rounded-md border border-[#1f3a64] bg-[#070d1c]/60 p-2.5">
            <span className={`w-2 h-2 rounded-full ${c.color}`} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white font-medium">{c.name}</div>
              <div className="text-[10px] text-slate-500">{c.status}</div>
            </div>
            <RefreshCw className="w-3 h-3 text-amber-300/70" />
          </div>
        ))}
      </div>

      <div className="flex flex-col items-center gap-1 text-amber-300/60" aria-hidden="true">
        <ArrowRight className="w-4 h-4" />
        <div className="w-px h-8 bg-gradient-to-b from-amber-300/60 via-amber-300/30 to-transparent" />
        <ArrowRight className="w-4 h-4 rotate-180" />
      </div>

      <div className="rounded-md border border-amber-300/40 bg-gradient-to-br from-amber-400/10 to-transparent p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">One inbox</div>
        <div className="text-white font-manrope font-bold text-sm">PROP VEIW</div>
        <div className="text-[10px] text-slate-400 mt-1">42 upcoming stays · 0 conflicts</div>
        <div className="mt-3 space-y-1">
          <div className="h-1.5 rounded-sm bg-rose-500/70" />
          <div className="h-1.5 rounded-sm bg-sky-500/70 w-4/5" />
          <div className="h-1.5 rounded-sm bg-indigo-500/70 w-2/3" />
          <div className="h-1.5 rounded-sm bg-emerald-500/70 w-1/2" />
        </div>
      </div>
    </div>
  </MockFrame>
);

/** Cleaning Kanban mockup — 3 lanes with pill statuses */
const CleaningKanbanMock = () => (
  <MockFrame url="app.propveiw.com/cleaning">
    <div className="text-white font-manrope font-semibold text-base mb-4">Cleaning Kanban</div>
    <div className="grid grid-cols-3 gap-3">
      {[
        {
          title: 'Pending',
          color: 'bg-amber-400',
          cards: [
            { prop: 'Sunset Beach', date: 'Feb 12', worker: 'Marisol' },
            { prop: 'Pine Ridge', date: 'Feb 14', worker: 'Elias' },
          ],
        },
        {
          title: 'In Progress',
          color: 'bg-sky-400',
          cards: [{ prop: 'Aspen 12A', date: 'Feb 11', worker: 'Priya · 1.2h', badge: '$58.50' }],
        },
        {
          title: 'Completed',
          color: 'bg-emerald-400',
          cards: [
            { prop: 'Bayside 3', date: 'Feb 10', worker: 'Jenna · 3.9h', badge: '$175.50' },
            { prop: 'Aspen 12A', date: 'Feb 09', worker: 'Priya · 2.1h', badge: '$98.00' },
          ],
        },
      ].map((lane) => (
        <div key={lane.title} className="rounded-md border border-[#1f3a64] bg-[#070d1c]/60 p-2.5">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-1.5 h-1.5 rounded-full ${lane.color}`} />
            <span className="text-[11px] text-slate-300 font-medium">
              {lane.title} <span className="text-slate-500">({lane.cards.length})</span>
            </span>
          </div>
          <div className="space-y-2">
            {lane.cards.map((c, i) => (
              <div key={`${lane.title}-${i}`} className="rounded border border-[#1f3a64]/70 bg-[#0c1530] p-2">
                <div className="text-[11px] text-white font-medium truncate">{c.prop}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{c.date}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-slate-400 truncate max-w-[70%]">{c.worker}</span>
                  {c.badge && (
                    <span className="text-[9px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
                      {c.badge}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </MockFrame>
);

/** Maintenance list mockup with priority + recurring badges */
const MaintenanceMock = () => (
  <MockFrame url="app.propveiw.com/maintenance">
    <div className="flex items-center justify-between mb-4">
      <div className="text-white font-manrope font-semibold text-base">Maintenance</div>
      <span className="text-[10px] text-slate-500">Auto-scheduled</span>
    </div>
    <div className="space-y-2.5">
      {[
        { title: 'Replace HVAC filter', prop: 'Sunset Beach House', date: 'Aug 11', priority: 'medium', recurring: 'Every 6 mo', status: 'pending' },
        { title: 'Smoke detector battery check', prop: 'Aspen 12A', date: 'Jul 09', priority: 'medium', recurring: 'Quarterly', status: 'pending' },
        { title: 'Gutter clean', prop: 'Pine Ridge', date: 'Jun 22', priority: 'low', recurring: 'Yearly', status: 'completed' },
      ].map((t) => (
        <div key={t.title} className="rounded-md border border-[#1f3a64] bg-[#070d1c]/60 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-white font-medium truncate">{t.title}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.prop} · {t.date}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                t.priority === 'medium'
                  ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                  : 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'
              }`}>{t.priority}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-violet-500/40 text-violet-300 bg-violet-500/10 flex items-center gap-1">
                <RefreshCw className="w-2.5 h-2.5" />
                {t.recurring}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                t.status === 'completed'
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                  : 'bg-slate-500/15 text-slate-300 border border-slate-500/40'
              }`}>
                {t.status}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </MockFrame>
);

/** Renovation card mockup with progress bar & milestones */
const RenovationMock = () => (
  <MockFrame url="app.propveiw.com/renovations">
    <div className="text-white font-manrope font-semibold text-base mb-3">Kitchen refresh — new cabinets &amp; counters</div>
    <div className="flex items-center gap-2 text-[10px] mb-4">
      <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40">In Progress</span>
      <span className="text-slate-500">Sunset Beach House · Peninsula Home Renovations</span>
    </div>

    <div className="mb-4">
      <div className="flex justify-between text-[11px] text-slate-400 mb-1.5">
        <span>Progress</span>
        <span className="text-amber-300 font-semibold">62%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#1f3a64]/60 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-200 w-[62%]" />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Budget</div>
        <div className="text-lg text-white font-manrope font-bold mt-0.5">$24,500</div>
      </div>
      <div className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Spent</div>
        <div className="text-lg text-emerald-300 font-manrope font-bold mt-0.5">$18,320</div>
      </div>
    </div>

    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Milestones</div>
      {[
        { name: 'Demo old cabinets', done: true },
        { name: 'Install new counters', done: true },
        { name: 'Paint & finish', done: false },
      ].map((m) => (
        <div key={m.name} className="flex items-center gap-2 text-[11px]">
          <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center border ${
            m.done ? 'bg-emerald-500/30 border-emerald-500/60' : 'border-slate-500/60'
          }`}>
            {m.done && <Check className="w-2.5 h-2.5 text-emerald-300" />}
          </span>
          <span className={m.done ? 'text-slate-500 line-through' : 'text-slate-200'}>{m.name}</span>
        </div>
      ))}
    </div>
  </MockFrame>
);

/** Revenue analytics mockup — KPI cards + bar chart */
const AnalyticsMock = () => (
  <MockFrame url="app.propveiw.com/analytics">
    <div className="text-white font-manrope font-semibold text-base mb-4">Revenue Analytics · 2026</div>
    <div className="grid grid-cols-4 gap-2 mb-5">
      {[
        { label: 'Revenue', val: '$25,553', c: 'text-white' },
        { label: 'Expenses', val: '$339', c: 'text-rose-300' },
        { label: 'Net', val: '$25,214', c: 'text-emerald-300' },
        { label: 'Occupancy', val: '78%', c: 'text-amber-300' },
      ].map((k) => (
        <div key={k.label} className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">{k.label}</div>
          <div className={`text-sm font-manrope font-bold ${k.c} mt-0.5`}>{k.val}</div>
        </div>
      ))}
    </div>

    <div className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-3">
      <div className="flex items-end gap-2 h-24">
        {[35, 55, 42, 70, 88, 62, 95, 76].map((h, i) => (
          <div key={`bar-${i}`} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t bg-gradient-to-t from-emerald-500/70 to-emerald-300"
              style={{ height: `${h}%` }}
            />
            <div className="text-[8px] text-slate-500">{`M${i + 1}`}</div>
          </div>
        ))}
      </div>
    </div>
  </MockFrame>
);

/** Invoices mockup — table with status pills */
const InvoicesMock = () => (
  <MockFrame url="app.propveiw.com/invoices">
    <div className="flex items-center justify-between mb-3">
      <div className="text-white font-manrope font-semibold text-base">Invoices</div>
      <span className="text-[10px] text-slate-500 flex items-center gap-1">
        <Sparkles className="w-3 h-3 text-amber-300" />
        Auto-drafted from tracked hours
      </span>
    </div>
    <div className="rounded-md border border-[#1f3a64] overflow-hidden">
      <div className="grid grid-cols-[1fr,90px,80px,60px] text-[9px] uppercase tracking-widest text-slate-500 bg-[#070d1c]/70 px-3 py-2">
        <div>Invoice</div>
        <div>Amount</div>
        <div>Due</div>
        <div>Status</div>
      </div>
      {[
        { n: 'INV-0142', c: 'Sunset Beach', amt: '$430', due: 'Feb 20', s: 'paid' },
        { n: 'INV-0141', c: 'Aspen 12A', amt: '$210', due: 'Feb 18', s: 'sent' },
        { n: 'INV-0140', c: 'Pine Ridge', amt: '$680', due: 'Feb 15', s: 'draft' },
        { n: 'INV-0139', c: 'Bayside 3', amt: '$395', due: 'Feb 12', s: 'overdue' },
      ].map((i) => (
        <div key={i.n} className="grid grid-cols-[1fr,90px,80px,60px] items-center px-3 py-2 border-t border-[#1f3a64]/70 text-[11px]">
          <div className="min-w-0">
            <div className="text-white truncate">{i.n}</div>
            <div className="text-[10px] text-slate-500 truncate">{i.c}</div>
          </div>
          <div className="text-slate-200 font-medium">{i.amt}</div>
          <div className="text-slate-400">{i.due}</div>
          <div>
            <span className={`text-[9px] px-1.5 py-0.5 rounded ${
              i.s === 'paid'
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                : i.s === 'sent'
                ? 'bg-sky-500/15 text-sky-300 border border-sky-500/40'
                : i.s === 'draft'
                ? 'bg-slate-500/15 text-slate-300 border border-slate-500/40'
                : 'bg-rose-500/15 text-rose-300 border border-rose-500/40'
            }`}>
              {i.s}
            </span>
          </div>
        </div>
      ))}
    </div>
  </MockFrame>
);

/** Staff seats & collaborators mockup */
const StaffMock = () => (
  <MockFrame url="app.propveiw.com/users">
    <div className="text-white font-manrope font-semibold text-base mb-1">Team &amp; Task Sharing</div>
    <div className="text-xs text-slate-400 mb-4">Add collaborators per task so people only see what they need.</div>

    <div className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-3 mb-4">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1.5">
        Sharing “Deep clean · Sunset Beach”
      </div>
      <div className="text-[11px] text-slate-400 mb-2">Assigned: Marisol V.</div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { name: 'Elias · Cleaner', on: true },
          { name: 'Jenna · Cleaner', on: false },
          { name: 'Priya · Cleaner', on: true },
          { name: 'Diego · Maint', on: false },
        ].map((u) => (
          <div key={u.name} className={`text-[10px] px-2 py-1.5 rounded border flex items-center justify-between gap-2 ${
            u.on
              ? 'bg-slate-900 border-slate-900 text-white'
              : 'border-[#1f3a64] text-slate-400'
          }`}>
            <span className="truncate">{u.name}</span>
            {u.on && <XIcon className="w-2.5 h-2.5" />}
          </div>
        ))}
      </div>
    </div>

    <div className="rounded border border-[#1f3a64] bg-[#070d1c]/60 p-3 flex items-center gap-3">
      <Lock className="w-4 h-4 text-amber-300" />
      <div>
        <div className="text-[11px] text-white font-medium">Everyone else on the team</div>
        <div className="text-[10px] text-slate-500">Sees the block on the calendar, but no details.</div>
      </div>
    </div>
  </MockFrame>
);

/** Photo Gallery mockup — booking-style before/after */
const PhotoGalleryMock = () => (
  <MockFrame url="app.propveiw.com/bookings">
    <div className="text-white font-manrope font-semibold text-base mb-1">Booking Photo Album</div>
    <div className="text-xs text-slate-400 mb-4">
      Cleaners upload check-out photos; guests get the welcome-ready shots.
    </div>
    <div className="space-y-4">
      {[
        {
          label: 'Welcome-ready',
          color: 'bg-emerald-500/70',
          gradients: [
            'from-amber-200/40 via-amber-500/20 to-slate-800/40',
            'from-sky-300/40 via-sky-500/20 to-slate-800/40',
            'from-emerald-300/40 via-emerald-500/20 to-slate-800/40',
          ],
        },
        {
          label: 'Check-out',
          color: 'bg-amber-400/80',
          gradients: [
            'from-rose-300/30 via-slate-500/20 to-slate-800/40',
            'from-slate-300/20 via-slate-500/20 to-slate-800/40',
          ],
        },
      ].map((row) => (
        <div key={row.label}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-1.5 h-1.5 rounded-full ${row.color}`} />
            <span className="text-[10px] uppercase tracking-widest text-slate-400">{row.label}</span>
            <span className="text-[10px] text-slate-500">· {row.gradients.length}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {row.gradients.map((g, i) => (
              <div
                key={`${row.label}-${i}`}
                className={`h-20 rounded-md border border-[#1f3a64] bg-gradient-to-br ${g}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </MockFrame>
);

/* -------------------------------------------------------------------------- */
/*  Feature blocks (zig-zag)                                                  */
/* -------------------------------------------------------------------------- */

const FEATURE_BLOCKS = [
  {
    icon: Calendar,
    tag: 'Unified Calendar',
    title: 'Every stay, cleaning, and repair on one drag-friendly grid.',
    body:
      'Colored blocks for bookings, cleanings, maintenance, landscaping, renovations, and owner-use — click to open, drag to reschedule. No more toggling tabs between three tools.',
    bullets: [
      'Property color-coding and legend',
      'Month + week views, mobile-friendly',
      'Instant conflict detection when you book',
    ],
    mock: <CalendarMock />,
  },
  {
    icon: RefreshCw,
    tag: 'iCal Sync (both ways)',
    title: 'Pull Airbnb, VRBO, Booking.com, Evolve. Push blocks back.',
    body:
      'Bring every channel’s reservations into one view and push the blocks you make in PROP VEIW back out — so no channel ever double-books you.',
    bullets: [
      'Refreshed every 15 minutes',
      'Read-only or two-way per feed',
      'Attach owner-use blocks to any listing',
    ],
    mock: <IcalSyncMock />,
  },
  {
    icon: Sparkles,
    tag: 'Turnover Cleanings',
    title: 'Auto-scheduled the moment a checkout hits the calendar.',
    body:
      'Every booking spawns a cleaning task. Your crew works from a Kanban with photos, work notes, and hours-tracked invoicing — you review and it turns into an invoice line item.',
    bullets: [
      'Photo uploads with a lock on privacy',
      'Hours in → invoice out',
      'Assign a worker in one tap',
    ],
    mock: <CleaningKanbanMock />,
  },
  {
    icon: Wrench,
    tag: 'Maintenance & Recurring',
    title: 'HVAC every 6 months. Gutters in spring. Set once, never forget.',
    body:
      'Give any task a cadence (weekly / monthly / quarterly / yearly / every N months) and PROP VEIW re-creates it on the right date. Priority + category tags keep the important things loud.',
    bullets: [
      'Landscaping, general, and repair sub-categories',
      'Estimated cost + tracked labor cost',
      'Recurring badges so the crew always knows',
    ],
    mock: <MaintenanceMock />,
  },
  {
    icon: Hammer,
    tag: 'Renovation Tracker',
    title: 'Budget vs. actuals, milestones, contractors, photo galleries.',
    body:
      'Multi-month projects have a way of sliding. PROP VEIW keeps the budget line, the milestone list, contractor contact, and before/after photos next to each other.',
    bullets: [
      'Milestone templates you can re-apply',
      'Auto-progress % from completed milestones',
      'Photo galleries with Before / After labels',
    ],
    mock: <RenovationMock />,
  },
  {
    icon: BarChart3,
    tag: 'Revenue Dashboard',
    title: 'The board you actually open, not the one you build in Sheets.',
    body:
      'Occupancy, ADR, RevPAR, net profit, expenses by category, revenue by property — all live, all filterable by year. If a number moves, you see it.',
    bullets: [
      'Revenue vs. expense trend per month',
      'Booking source breakdown',
      'Property-level profit ranking',
    ],
    mock: <AnalyticsMock />,
  },
  {
    icon: Receipt,
    tag: 'Expenses & Invoices',
    title: 'Snap receipts, tag categories, generate invoices from tracked hours.',
    body:
      'Tax time stops being a scavenger hunt. Every worker’s tracked hours can be turned into a proper invoice with your notes, and the PDF is one click away.',
    bullets: [
      'Downloadable, styled PDFs',
      'Recurring invoice rules',
      'Draft / Sent / Paid / Overdue lifecycle',
    ],
    mock: <InvoicesMock />,
  },
  {
    icon: Users,
    tag: 'Staff seats & task sharing',
    title: 'Everyone sees the block. Only the right people see the details.',
    body:
      'Invite cleaners, handymen, landscapers, contractors. They see tasks assigned to them, plus any you explicitly share via the “Share with” picker. Everyone else sees a locked placeholder on the calendar.',
    bullets: [
      'Per-task collaborators for teams that work together',
      'Managers/owners always see everything',
      'Bookings stay fully visible for coordination',
    ],
    mock: <StaffMock />,
  },
  {
    icon: ImageIcon,
    tag: 'Photo Albums everywhere',
    title: 'Visual evidence for every stay, every clean, every reno phase.',
    body:
      'Attach labeled photos to bookings (Check-out / Welcome-ready), properties, work orders, and renovation milestones. All stored in Emergent-managed object storage — no expiring links.',
    bullets: [
      'Camera capture from mobile',
      'Preset labels per context',
      'Uploader-only or manager-only delete',
    ],
    mock: <PhotoGalleryMock />,
  },
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
        <BgGlow className="-top-32 -left-32 w-[520px] h-[520px] bg-amber-400/10" />
        <BgGlow className="top-40 -right-40 w-[560px] h-[560px] bg-sky-500/10" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex flex-col items-center gap-4 mb-6">
            <GoldRule />
            <SectionLabel>The full tour</SectionLabel>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-manrope font-black tracking-tight text-white leading-[1.05]">
            Every corner of PROP VEIW,
            <br />
            <span className="bg-gradient-to-r from-amber-200 via-amber-100 to-sky-200 bg-clip-text text-transparent">
              shown in daylight.
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Nine deep-dives, one dashboard. Skim the highlights or pull each screen apart —
            you&rsquo;ll see how bookings, cleanings, renovations, and the money side finally
            talk to each other.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold shadow-lg shadow-amber-500/20 h-12 px-6"
                data-testid="features-hero-cta"
              >
                Start free trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button
                size="lg"
                variant="outline"
                className="border-[#1f3a64] text-slate-200 hover:bg-[#0c1530] hover:border-[#2a4a7a] bg-transparent h-12 px-6"
                data-testid="features-hero-back-btn"
              >
                Back to overview
              </Button>
            </Link>
          </div>
        </div>

        {/* Hero mockup */}
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 mt-14 lg:mt-16">
          <div className="absolute inset-x-8 -bottom-6 h-16 bg-amber-400/10 blur-3xl rounded-full" />
          <div className="relative">
            <CalendarMock />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#1f3a64]/80 bg-[#0c1530]/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { k: 'Bookings synced', v: '480k+' },
            { k: 'Cleanings scheduled', v: '38k+' },
            { k: 'Renovation budgets tracked', v: '$42M' },
            { k: 'Data isolation', v: 'Per-org' },
          ].map((s) => (
            <div key={s.k}>
              <div className="font-manrope font-bold text-2xl text-white">{s.v}</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-widest">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ZIG-ZAG FEATURE BLOCKS */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 lg:py-28 space-y-20 lg:space-y-28">
          {FEATURE_BLOCKS.map((f, idx) => {
            const flip = idx % 2 === 1;
            const Icon = f.icon;
            return (
              <div
                key={f.tag}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  flip ? 'lg:[&>*:first-child]:order-2' : ''
                }`}
                data-testid={`feature-block-${idx}`}
              >
                {/* Copy */}
                <div className="max-w-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-amber-400/10 border border-amber-300/40 text-amber-300">
                      <Icon className="w-4 h-4" />
                    </span>
                    <SectionLabel>{f.tag}</SectionLabel>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-manrope font-bold tracking-tight text-white leading-tight">
                    {f.title}
                  </h2>
                  <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
                    {f.body}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="w-4 h-4 mt-0.5 text-amber-300 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Mock */}
                <div className="relative">
                  <BgGlow
                    className={`w-64 h-64 ${
                      flip ? '-right-16 -bottom-8 bg-sky-500/10' : '-left-16 -bottom-8 bg-amber-400/10'
                    }`}
                  />
                  <div className="relative">{f.mock}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* COMPARISON MINI-TABLE */}
      <section className="border-t border-[#1f3a64]/80 bg-[#0c1530]/40 py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-1 h-6 bg-gradient-to-b from-amber-300 to-sky-300" />
            <SectionLabel>vs. the pile of tools</SectionLabel>
          </div>
          <h2 className="text-3xl sm:text-4xl font-manrope font-bold tracking-tight text-white leading-tight max-w-xl">
            Cancel three subscriptions on the way to onboarding.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl text-base sm:text-lg">
            Most hosts stitch together a PMS, a cleaning app, a spreadsheet, and a task list.
            PROP VEIW replaces every one of them with a single, org-isolated dashboard.
          </p>

          <div className="mt-10 overflow-hidden rounded-lg border border-[#1f3a64]">
            <div className="grid grid-cols-4 text-[11px] uppercase tracking-widest text-slate-500 bg-[#070d1c]/70">
              <div className="p-4">Capability</div>
              <div className="p-4 text-amber-300 border-l border-[#1f3a64]">PROP VEIW</div>
              <div className="p-4 border-l border-[#1f3a64]">Generic PMS</div>
              <div className="p-4 border-l border-[#1f3a64]">Spreadsheet</div>
            </div>
            {[
              ['Unified calendar for bookings + tasks', true, 'partial', false],
              ['Auto-created turnover cleanings', true, false, false],
              ['Recurring maintenance rules', true, false, 'manual'],
              ['Renovation project + budget tracker', true, false, 'partial'],
              ['Hours → Invoice', true, false, 'manual'],
              ['Per-task collaborator sharing', true, false, false],
              ['Photo albums with labels', true, 'partial', false],
              ['Live revenue dashboard', true, 'partial', false],
            ].map((row, i) => (
              <div
                key={row[0]}
                className={`grid grid-cols-4 text-sm border-t border-[#1f3a64] ${
                  i % 2 === 0 ? 'bg-[#0c1530]/30' : 'bg-[#070d1c]/40'
                }`}
              >
                <div className="p-4 text-slate-200">{row[0]}</div>
                {row.slice(1).map((v, j) => (
                  <div key={`${row[0]}-col-${j}`} className="p-4 border-l border-[#1f3a64]">
                    {v === true ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs">
                        <Check className="w-3.5 h-3.5" /> Yes
                      </span>
                    ) : v === false ? (
                      <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs">
                        <XIcon className="w-3.5 h-3.5" /> No
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-amber-300 text-xs">
                        <ChevronRight className="w-3.5 h-3.5" />
                        {v === 'partial' ? 'Partial' : 'Manual'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PULL-QUOTE */}
      <section className="relative py-20 lg:py-24">
        <BgGlow className="top-8 left-1/2 -translate-x-1/2 w-[600px] h-[240px] bg-amber-400/10" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <GoldRule />
          <blockquote className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-manrope font-semibold text-white leading-tight">
            &ldquo;First quarter where the cleanings, repairs, and books actually agreed with each other.&rdquo;
          </blockquote>
          <div className="mt-6 text-slate-400 text-sm">
            <span className="text-amber-200 font-medium">Derek Aigbe</span> · 4 doors · Asheville
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative border-t border-[#1f3a64]/80 bg-gradient-to-b from-[#070d1c] to-[#0c1530]/60 py-24">
        <BgGlow className="top-8 left-24 w-96 h-64 bg-amber-400/10" />
        <BgGlow className="bottom-8 right-24 w-96 h-64 bg-sky-500/10" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <SectionLabel>Ready when you are</SectionLabel>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-tight text-white">
            Bring your properties, keep your calm.
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto text-base sm:text-lg">
            Free during beta — full access, no credit card. Import your first calendar in about
            five minutes.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold shadow-lg shadow-amber-500/20 h-12 px-6"
                data-testid="features-bottom-cta"
              >
                Start free trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                size="lg"
                variant="outline"
                className="border-[#1f3a64] text-slate-200 hover:bg-[#0c1530] hover:border-[#2a4a7a] bg-transparent h-12 px-6"
                data-testid="features-bottom-pricing-btn"
              >
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
